<script setup lang="ts">
const props = defineProps({
    hasPass: {
        type: Boolean,
        required: true
    },
    isTeslaLinkConfirmed: {
        type: Boolean,
        required: true
    },
    isTeslaLinkInitiated: {
        type: Boolean,
        required: true
    }
})

const { t } = useI18n()

const currentIndex = ref(0)

const slides = ref([
    {
        showClassStart: false,
        showClassEnd: false,
        showClassPrev: false,
        showClassNext: false,
        img: '/images/tesla/TeslaChargemap.svg',
        title: t('tesla.modal.slide1')
    },
    {
        showClassStart: false,
        showClassEnd: false,
        showClassPrev: false,
        showClassNext: false,
        img: '/images/tesla/OrderPass.svg',
        title: t('tesla.modal.slide2')
    },
    {
        showClassStart: false,
        showClassEnd: false,
        showClassPrev: false,
        showClassNext: false,
        img: '/images/tesla/Download.svg',
        title: t('tesla.modal.slide3')
    },
    {
        showClassStart: false,
        showClassEnd: false,
        showClassPrev: false,
        showClassNext: false,
        img: '/images/tesla/Wallet.svg',
        title: t('tesla.modal.slide4')
    },
    {
        showClassStart: false,
        showClassEnd: false,
        showClassPrev: false,
        showClassNext: false,
        img: '/images/tesla/Email.svg',
        title: t('tesla.modal.slide5')
    },
    {
        showClassStart: false,
        showClassEnd: false,
        showClassPrev: false,
        showClassNext: false,
        img: '/images/tesla/Charge.svg',
        title: t('tesla.modal.slide6.0')
    }
])

const timer = ref<NodeJS.Timeout | string | number | undefined>(undefined)
const timeTotal = ref(300)
const timePhaseOne = ref(10)

onMounted(() => {
    if (props.hasPass) {
        slides.value.splice(1, 1)
    }

    if (props.isTeslaLinkInitiated) {
        slides.value = [
            {
                showClassStart: false,
                showClassEnd: false,
                showClassPrev: false,
                showClassNext: false,
                img: '/images/tesla/Email.svg',
                title: t('tesla.modal.slide5')
            }
        ]
    }

    if (props.isTeslaLinkConfirmed) {
        slides.value = [
            {
                showClassStart: false,
                showClassEnd: false,
                showClassPrev: false,
                showClassNext: false,
                img: '/images/tesla/Charge.svg',
                title: t('tesla.modal.slide6.activated')
            }
        ]
    }
})

onUnmounted(() => {
    if (timer.value) {
        clearInterval(timer.value)
    }
})

const sleep = (milliseconds: number): Promise<any> =>
    new Promise((resolve) => setTimeout(resolve, milliseconds))

const setCustomInterval = () => {
    clearInterval(timer.value)
    timer.value = setInterval(() => {
        changeSlide('next', true, currentIndex.value)
    }, 5000)
}

const getIndexSecondSlide = (typeChangeSlide: string) => {
    let secondIndex =
        currentIndex.value + 1 <= slides.value.length - 1
            ? currentIndex.value + 1
            : 0

    if (typeChangeSlide === 'prev') {
        secondIndex =
            currentIndex.value - 1 >= 0
                ? currentIndex.value - 1
                : slides.value.length - 1
    }

    return secondIndex
}

const updateShowClass = (index: number, value: boolean, type: string) => {
    if (!slides.value || !slides.value[index]) {
        return
    }

    const typeClass = type === 'next' ? 'showClassStart' : 'showClassEnd'
    slides.value[index][typeClass] = value
}

const updateShowClassPrevNext = (
    index: number,
    value: boolean,
    type: string
) => {
    if (!slides.value || !slides.value[index]) {
        return
    }

    const typeClass = type === 'prev' ? 'showClassPrev' : 'showClassNext'
    slides.value[index]![typeClass] = value
}

const animationPhaseOne = async (typeChangeSlide: string) => {
    const indexSecondSlide = getIndexSecondSlide(typeChangeSlide)
    const indexList = [currentIndex.value, indexSecondSlide]
    updateShowClassPrevNext(indexSecondSlide, true, typeChangeSlide)
    await sleep(timePhaseOne.value)
    indexList.forEach((index: number) =>
        updateShowClass(index, true, typeChangeSlide)
    )
}

const animationPhaseTwo = (typeChangeSlide: string) => {
    slides.value?.forEach((_, indexSlide) => {
        updateShowClass(indexSlide, false, typeChangeSlide)
        updateShowClassPrevNext(indexSlide, false, typeChangeSlide)
    })
}

const changeSlide = async (
    typeChangeSlide: string,
    isInterval: boolean,
    index: number
) => {
    const indexDiff = Math.abs(index - currentIndex.value)

    if (indexDiff > 1) {
        let i = 0
        while (i < indexDiff - 1) {
            await changeSlide(typeChangeSlide, true, currentIndex.value)
            i += 1
        }
    }

    if (!isInterval) {
        setCustomInterval()
    }
    await animationPhaseOne(typeChangeSlide)
    await sleep(timeTotal.value - timePhaseOne.value)
    animationPhaseTwo(typeChangeSlide)

    if (typeChangeSlide === 'prev') {
        currentIndex.value =
            currentIndex.value === 0
                ? slides.value.length - 1
                : currentIndex.value - 1
    } else {
        currentIndex.value =
            currentIndex.value === slides.value.length - 1
                ? 0
                : currentIndex.value + 1
    }
}

const goToSlide = (index: number) => {
    if (index < currentIndex.value) {
        changeSlide('prev', false, index)
    } else if (index > currentIndex.value) {
        changeSlide('next', false, index)
    }
}
</script>

<template>
    <div class="flex flex-col justify-center items-center">
        <div class="flex flex-col justify-center items-center font-inter">
            <div class="mx-4">
                <div
                    v-for="(item, index) in slides"
                    :id="`slide-${index}`"
                    :key="index"
                    class="transition-transform duration-300"
                    :class="{
                        hidden: index !== currentIndex,
                        active: index === currentIndex,
                        'carousel-item-start': item.showClassStart,
                        'carousel-item-end': item.showClassEnd,
                        'carousel-item-next': item.showClassNext,
                        'carousel-item-prev': item.showClassPrev
                    }"
                >
                    <img
                        class="m-auto"
                        loading="lazy"
                        :src="item.img"
                        :alt="item.title"
                    />
                    <div
                        :id="`text${index}`"
                        :key="`text${index}`"
                        class="relative overflow-hidden w-full mt-4 px-4 text-center"
                        :class="{ 'h-24': slides.length > 1 }"
                    >
                        <span
                            class="font-normal text-seventeen-exploitation leading-tight text-gray-l-700 tracking-light"
                        >
                            {{ item.title }}
                        </span>
                    </div>
                </div>
            </div>
            <div
                v-if="slides.length > 1"
                class="flex justify-center items-center h-4 mt-4"
            >
                <span
                    v-for="(item, index) in slides"
                    :id="`slideDot${index}`"
                    :key="`slideDot${index}`"
                    class="h-2 w-2 rounded-full mx-1 cursor-pointer"
                    :class="{
                        'bg-blue-400': index === currentIndex,
                        'bg-gray-200': index !== currentIndex
                    }"
                    @click="goToSlide(index)"
                ></span>
            </div>
        </div>
    </div>
</template>
